import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import Select from "react-select";
import Loader from "react-spinner-loader";
import * as Yup from "yup";
import {
    Button,
    Card,
    Col,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Form,
    FormFeedback,
    Label,
    Row,
    Table,
    UncontrolledDropdown,
    UncontrolledTooltip,
} from "reactstrap";
import { decryptedValue, uId, isPermission } from "common/utility";
import ToastDialog from "components/Common/ToastDialog";
import { getPoc, getAnchorPoc, addAnchorPoc, deleteAnchorPoc } from "http/fundLync/poc.helper";
import CommonDialog from "components/Common/CommonDialog";

const AnchorContact = ({ anchorList,onContactAdded,isOpen,onAccordionToggle}) => {
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [isAddPOCOpen, setAddPOCOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [loader, setLoader] = useState(false);
    const [pocList, setPocList] = useState([]);
    const [anchorPocList, setAnchorPocList] = useState([]);
    const urlParams = new URLSearchParams(window.location.search);
    const [pocData, setPoc] = useState(null);
    const [toastInfo, setToastInfo] = useState({ showToast: false });
    const [entityId, setEntityId] = useState('')
    const [encodedId, setEncodedId] = useState('')
    const [showCommonDialog, setShowCommonDialog] = useState({ isShow: false });
    const [selectedEntityName, setSelectedEntityName] = useState("");
    const [selectedMulti, setSelectedMulti] = useState([]);
    const [isRedirectPermission, setIsRedirectPermission] = useState(false);

    useEffect(() => {
        const userDetails = decryptedValue(localStorage.getItem("user"));
        if (userDetails.entityType === "Partner" || userDetails.entityType === "Owner") {
            setEncodedId(urlParams.get("id"))
            setEntityId(atob(encodedId))
        } else {
            setEncodedId(entityId)
            setEntityId(userDetails.entityId)
        }
    }, [entityId, encodedId])

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            anchorId: (pocData && pocData[0].anchorId) || "",
            contactId: (pocData && pocData[0].contactId) || "",
            id: (pocData && pocData[0].id) || "",
        },
        validationSchema: Yup.object({
            anchorId: Yup.string().required("Anchor name is Required"),
            contactId: Yup.string().required("Contact Name is Required"),
        }),
        onSubmit: (values) => {
            const data = {
                id: +values.id,
                anchorId: +values.anchorId,
                counterpartyId: entityId,
            };
            data.contactId = values.contactId.split(',')
            addAnchorPoc(data)
                .then((response) => {
                    if (response.code == 201 && response.status) {
                        setToastInfo({
                            message: response?.message || "Anchor Contact Details Saved",
                            showToast: true,
                            role: "success",
                        });
                        validation.resetForm();
                        setAddPOCOpen(false);
                        setPoc(null);
                        setSelectedEntityName('')
                        setSelectedMulti([])
                        onContactAdded();
                        getAnchorContactsList(entityId)
                    } else {
                        const { message } = response;
                        setToastInfo({
                            message: message,
                            showToast: true,
                            role: "error",
                        });
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        },
    });

    useEffect(() => {
        checkPermissions();
    }, []);

    useEffect(() => {
        if (isOpen) {
            getAnchorContactsList(entityId)
        }
    }, [isOpen, entityId]);

    const handleCustomerClicks = () => {
        setAddPOCOpen(true);
        setIsEdit(false);
        setPoc(null);
    };

    const handleCancelrClicks = () => {
        validation.resetForm();
        setAddPOCOpen(false);
        setPoc(null);
        setSelectedEntityName('')
        setSelectedMulti([])
    };

    const toggleDropdown = () => {
        setDropdownOpen(!isDropdownOpen);
        setAddPOCOpen(false);
        setIsEdit(false);
        if (onAccordionToggle) {
            onAccordionToggle(!isDropdownOpen);
          }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
    };

    const deleteDetail = (id) => {
        const message = 'Are you sure you want to delete this data?'
        const data = { id, status: 0 }
        setShowCommonDialog({ isShow: true, message, data })
    };

    const dialogActionAccept = (action, data) => {
        if (action == 1) {
            setShowCommonDialog({ isShow: false })
            deleteAnchorPoc(data)
                .then((response) => {
                    if (response.code == 201 && response.status) {
                        setToastInfo({
                            message: "Anchor Contact Deleted",
                            showToast: true,
                            role: "success",
                        });
                        getAnchorContactsList(entityId)
                    } else {
                        const { message } = response;
                        setToastInfo({
                            message: message,
                            showToast: true,
                            role: "error",
                        });
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        } else {
            setShowCommonDialog({ isShow: false })
        }
    }

    const getAnchorContacts = (anchorId) => {
        getPoc({ entityId: anchorId })
            .then((response) => {
                if (response.status) {
                    const transformedPocList = response?.data?.data.map((poc) => ({
                        value: poc.id,
                        label: `${poc.firstname || ''} ${poc.lastname || ''}`.trim()
                    }));

                    const matchedData = anchorPocList.map(anchorPoc => {
                        const matchedItem = response?.data?.data?.find(
                            item => item.id == anchorPoc.contactId
                        );

                        return matchedItem ? { label: `${anchorPoc.firstname || ''} ${anchorPoc.lastname || ''}`.trim(), value: anchorPoc.contactId } : null;
                    }).filter(item => item !== null);

                    const newData = [];
                    matchedData.forEach((ele) => {
                        newData.push(ele.value);
                    });
                    validation.setFieldValue('contactId', newData.join(","))
                    setSelectedMulti(matchedData);
                    setPocList(transformedPocList);
                }
                else {
                    setPocList([]);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }

    const getAnchorContactsList = (anchorId) => {
        getAnchorPoc({ entityId: anchorId })
            .then((response) => {
                if (response.status && response?.data?.length > 0) {
                    setAnchorPocList(response?.data);
                }
                else {
                    setAnchorPocList([]);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }

    const handleMulti1 = (selectedOptions) => {
        const newData = [];
        selectedOptions.forEach((ele) => {
            newData.push(ele.value);
        });
        validation.setFieldValue('contactId', newData.join(","))
        setSelectedMulti(selectedOptions);
    };

    const truncateText = (text, charLimit) => {
        if (text && text.length > charLimit) {
            const truncated = text.substring(0, charLimit) + "...";
            return truncated;
        }
        return text;
    };

    const handleMouseEnter = (event) => {
        event.target.style.color = "#34c38f";
        event.target.style.textDecoration = "underline";
    };

    const handleMouseLeave = (event) => {
        event.target.style.color = "#21aa47";
        event.target.style.textDecoration = "none";
    };

    const navigateToEditEntity = (entityId) => {
        const user = decryptedValue(localStorage.getItem("user"));
        if (entityId) {
            const encryptedId = btoa(entityId.toString());
            const url = `/${user.roleName}/entity-Dashboard?id=${encodeURIComponent(encryptedId)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    const checkPermissions = async () => {
        const isPerm = await isPermission("entity_detail", "full_access");
        setIsRedirectPermission(isPerm);
    };

    return (
        <React.Fragment>
            <Row lg="12">
                <div>
                    <Card style={{ padding: "10px" }}>
                        <div
                            className="d-flex justify-content-between mt-1"
                            style={{ cursor: "pointer" }}
                            onClick={toggleDropdown}
                        >
                            <div>
                                <h5>Anchor Contacts</h5>
                            </div>
                            <div className="me-2">
                                <i
                                    className={`fas fa-angle-${isDropdownOpen ? "up" : "down"}`}
                                    style={{ fontSize: "20px" }}
                                ></i>
                            </div>
                        </div>

                        {isAddPOCOpen && (
                            <div>
                                <div>
                                    <Form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            validation.handleSubmit();
                                            handleSubmit(e)
                                            return false;
                                        }}
                                    >
                                        <Row className="mb-3">
                                            <Col sm="12" md="6">
                                                <Label className="form-label grey mt-mobile">
                                                    Anchor Name*
                                                </Label>
                                                <Select
                                                    options={anchorList?.map((option, index) => ({
                                                        label: option.label,
                                                        value: option.value,
                                                    }))}
                                                    className={`select-dropdown ${validation.touched.anchorId &&
                                                        validation.errors.anchorId
                                                        ? "is-invalid"
                                                        : ""
                                                        }`}
                                                    name="anchorId"
                                                    id="anchorId"
                                                    placeholder={"Enter Entity Name"}
                                                    value={selectedEntityName}
                                                    onChange={(selectedOption) => {
                                                        setSelectedEntityName(selectedOption)
                                                        getAnchorContacts(selectedOption?.value)
                                                        validation.setFieldValue('anchorId', selectedOption?.value);
                                                    }}
                                                    onBlur={() => {
                                                        validation.setFieldTouched('anchorId', true);
                                                    }}
                                                />
                                                {validation.touched.anchorId &&
                                                    validation.errors.anchorId && (
                                                        <FormFeedback>
                                                            {validation.errors.anchorId}
                                                        </FormFeedback>
                                                    )}
                                            </Col>
                                            <Col sm="12" md="6">
                                                <Label className="form-label grey">
                                                    Contact Name*
                                                </Label>
                                                <Select
                                                    name="contactId"
                                                    value={selectedMulti}
                                                    isMulti={true}
                                                    onChange={handleMulti1}
                                                    options={pocList}
                                                    onBlur={validation.handleBlur}
                                                    className={`select-dropdown ${validation.touched.contactId &&
                                                        validation.errors.contactId
                                                        ? "is-invalid"
                                                        : ""
                                                        }`}
                                                />
                                                {validation.touched.contactId &&
                                                    validation.values.contactId == "" && (
                                                        <p
                                                            style={{
                                                                color: "rgb(245,116,116)",
                                                                fontSize: "11px",
                                                            }}
                                                        >
                                                            {validation.errors.contactId}
                                                        </p>
                                                    )}
                                            </Col>
                                        </Row>
                                        <Row className="mb-3">
                                            <Col>
                                                <div className="text-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary save-customer me-2"
                                                        onClick={handleCancelrClicks}
                                                    >
                                                        Cancel
                                                    </button>
                                                    {isEdit ? (
                                                        <button
                                                            type="submit"
                                                            className="btn btn-success save-customer"
                                                        >
                                                            Update
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="submit"
                                                            className="btn btn-success save-customer"
                                                        >
                                                            Save
                                                        </button>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </div>
                            </div>
                        )}

                        {isDropdownOpen && (
                            <Row>
                                <div className="text-sm-end">
                                    <Button
                                        type="button"
                                        color="success"
                                        className="btn-rounded mb-2 me-2"
                                        onClick={handleCustomerClicks}
                                    >
                                        <i className="mdi mdi-plus me-1" />
                                        New Anchor Contact
                                    </Button>
                                </div>
                                <Col xs="12" className="mt-1 mb-2">
                                    <div className="table-responsive">
                                        <Table className="table table-bordered table-striped mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Id</th>
                                                    <th>Anchor Name</th>
                                                    <th>Contact Name</th>
                                                    <th>Email</th>
                                                    <th>Contact number</th>
                                                    <th>Designation</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {anchorPocList?.length == 0 ? (
                                                    <tr>
                                                        <td colSpan={8} className="text-center">
                                                            No data found
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    anchorPocList?.map((elem, index) => (
                                                        <tr key={uId()}>
                                                            <td>{index + 1}</td>
                                                            {isRedirectPermission && (<td title={elem.Anchorame} style={{ width: '250px' }}>
                                                                <a
                                                                    href="#"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        navigateToEditEntity(elem.anchorId);
                                                                    }}
                                                                    onMouseEnter={handleMouseEnter}
                                                                    onMouseLeave={handleMouseLeave}
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        color: "#21aa47",
                                                                        width: "170px",
                                                                        textDecoration: "none",
                                                                    }}
                                                                    title={elem.Anchorame || ""}
                                                                >
                                                                    <span>{truncateText(elem.Anchorame, 30) || ""}</span>
                                                                </a>
                                                            </td>)}
                                                            {!isRedirectPermission && (<td title={elem.Anchorame} style={{ width: '250px' }}>
                                                                {truncateText(elem.Anchorame, 30) || ""}
                                                            </td>)}
                                                            <td style={{width:'270px'}}>
                                                                {`${elem.firstname || ''} ${elem.lastname || ''}`.trim()}
                                                            </td>
                                                            <td style={{width:'200px'}}>{elem.email}</td>
                                                            <td style={{width:'150px'}}>{elem.contactNumber}</td>
                                                            <td>{elem.designation}</td>
                                                            <td>
                                                                <UncontrolledDropdown>
                                                                    <DropdownToggle
                                                                        tag="a"
                                                                        className="card-drop"
                                                                    >
                                                                        <i className="mdi mdi-dots-horizontal font-size-18"></i>
                                                                    </DropdownToggle>
                                                                    <DropdownMenu className="dropdown-menu-end">
                                                                        <DropdownItem
                                                                            onClick={() => {
                                                                                deleteDetail(elem.id);
                                                                            }}
                                                                        >
                                                                            <i
                                                                                className="mdi mdi-delete font-size-16 text-success me-1"
                                                                                id="deletetooltip"
                                                                            ></i>
                                                                            Delete
                                                                            <UncontrolledTooltip
                                                                                placement="top"
                                                                                target="deletetooltip"
                                                                            >
                                                                                Delete
                                                                            </UncontrolledTooltip>
                                                                        </DropdownItem>
                                                                    </DropdownMenu>
                                                                </UncontrolledDropdown>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </Card>
                </div>
            </Row>
            <ToastDialog
                message={toastInfo.message || ""}
                showToast={toastInfo.showToast}
                role={toastInfo.role}
                onCloseFn={() => setToastInfo({ showToast: false })}
            ></ToastDialog>
            <Loader show={loader} type="box" message="Fetching Data" />
            {
                showCommonDialog &&
                <CommonDialog
                    isShow={showCommonDialog.isShow}
                    message={showCommonDialog.message} data={showCommonDialog.data}
                    postitiveBtn={'Yes'} negativeBtn={'No'} onCloseFn={dialogActionAccept}
                />
            }
        </React.Fragment>
    );
};

export default AnchorContact;
